import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { performLogout } from "@/services/api";

/**
 * useLogout Hook
 *
 * Handles proper logout:
 * 1. Aborts all pending requests
 * 2. Broadcasts logout to other tabs (cross-tab sync)
 * 3. Calls backend /api/v1/auth/logout to clear httpOnly cookie and revoke tokens
 * 4. Clears memory state (access token, user)
 * 5. Redirects to login page
 *
 * Usage:
 * ```jsx
 * function Header() {
 *   const logout = useLogout();
 *
 *   return (
 *     <Button onClick={logout}>Logout</Button>
 *   );
 * }
 * ```
 */
export const useLogout = () => {
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    // Use centralized logout that handles:
    // - Aborting pending requests
    // - Cross-tab sync
    // - API call
    // - State cleanup
    await performLogout();

    // Redirect to login
    navigate("/login", { replace: true });
  }, [navigate]);

  return handleLogout;
};

export default useLogout;
